import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from './admin/useAdminApi';

/**
 * Self-serve phone directory + text authorization hooks (Settings →
 * Notifications). Backed by the customer-phones function, which pins scope to
 * the caller's active customer server-side. These are the numbers our phone
 * line (Chloe) recognizes callers by, and the per-number SMS opt-in record.
 */

export interface CustomerPhone {
  id: string;
  phone: string;
  phone_digits: string | null;
  label: string | null;
  source: 'qbo' | 'billandpay' | 'portal_user' | 'manual' | 'apply';
  sms_consent_at: string | null;
  sms_opt_out_at: string | null;
  created_at: string;
}

const PHONES_KEY = ['customer-phones'] as const;

export function useCustomerPhones() {
  return useQuery({
    queryKey: PHONES_KEY,
    queryFn: () =>
      adminFetch<{ ok: true; phones: CustomerPhone[]; consent_version: string }>('customer-phones'),
    staleTime: 30_000,
  });
}

export function useAddCustomerPhone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { phone: string; label?: string; sms_consent?: boolean }) =>
      adminFetch<{ ok: true }>('customer-phones', {
        method: 'POST',
        body: JSON.stringify({ action: 'add', ...input }),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: PHONES_KEY }),
  });
}

export function useSetPhoneConsent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; consent: boolean }) =>
      adminFetch<{ ok: true }>('customer-phones', {
        method: 'POST',
        body: JSON.stringify({ action: 'set_consent', ...input }),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: PHONES_KEY }),
  });
}

export function useRemoveCustomerPhone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string }) =>
      adminFetch<{ ok: true }>('customer-phones', {
        method: 'POST',
        body: JSON.stringify({ action: 'remove', ...input }),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: PHONES_KEY }),
  });
}
